import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import scrollToComponent from 'react-scroll-to-component';
import { injectIntl, defineMessages } from 'react-intl';

import CircularProgress from '@material-ui/core/CircularProgress';
import Snackbar from '@material-ui/core/Snackbar';

import CheckoutBillTable from './tables/CheckoutBillTable';
import CheckoutOrderingPaymentStepPaymentMethods from './CheckoutOrderingPaymentStepPaymentMethods';
import CheckoutOrderingPaymentStepAddress from './CheckoutOrderingPaymentStepAddress';
import CheckoutOrderingPaymentStepCheckbox from './CheckoutOrderingPaymentStepCheckbox';
import { FormattedMessageWrappedInSpan } from '../../misc';

import BraintreeClientTokenMutation from '../../../mutations/BraintreeClientTokenMutation';

import { initPaypalButton } from '../../../utils/initPaypalButton';
import { includesPayPal, getRealAmount } from '../../../utils/common';
import { trackEvent } from '../../../utils/ga-tracking';

const PAYPAL = 'PayPal';

class CheckoutOrderingPaymentStep extends Component {
  constructor(props) {
    super(props);
    const { order, availablePaymentMethods } = this.props.cart;

    this.state = {
      acceptsPrivacy: false,
      acceptsSgContract: false,
      addressCollapsed: true,
      errorMsgId: 'paymentMethodNotDefinedError',
      hasPaypal: includesPayPal(availablePaymentMethods),
      hasPayPalCheckoutBtn: false,
      isLoadingPayPalCheckout: false,
      paymentMethod: order.payment ? order.payment.paymentMethod : undefined,
      paymentRequired: props.cart.paymentRequired,
      showSnackbar: false,
    };
  }

  componentDidMount() {
    scrollToComponent(this.paymentMethodDiv);
  }

  handlePaymentMethodChanged = pm => {
    this.setState({
      showSnackbar: false,
      paymentMethod: pm,
    });

    if (pm.name === PAYPAL && !this.state.hasPayPalCheckoutBtn) {
      this._renderPayPalCheckoutBtn();
    }
  };

  handleCouponChanged = (isPaymentRequired) => {
    this.setState({ paymentRequired: isPaymentRequired });
  };

  handlePrivacyCheckboxChanged = (event, isInputChecked) => {
    this.setState({ acceptsPrivacy: isInputChecked });
  };

  handleSgContractCheckboxChanged = (event, isInputChecked) => {
    this.setState({ acceptsSgContract: isInputChecked });
  };

  handleCompleteCheckout = type => {
    const { paymentMethod, acceptsPrivacy, acceptsSgContract, paymentRequired } = this.state;
    if (paymentRequired && !paymentMethod) {
      this.setState({
        showSnackbar: true,
        errorMsgId: 'paymentMethodNotDefinedError',
      });
      scrollToComponent(this.paymentMethodDiv);
      return;
    }
    if (!acceptsPrivacy) {
      this.setState({
        showSnackbar: true,
        errorMsgId: 'privacyCheckboxNotCheckedError',
      });
      return;
    }
    if (!acceptsSgContract) {
      this.setState({
        showSnackbar: true,
        errorMsgId: 'sgContractCheckboxNotCheckedError',
      });
      return;
    }

    if (type === 'pay') {
      this.props.onNextStep(paymentMethod);
    } else if (type === 'free') {
      this.props.onConfirmCheckout();
    }
  };

  handleCloseSnackbar = () => {
    this.setState({ showSnackbar: false });
  };

  handleShowAddress = () => {
    this.setState({
      addressCollapsed: !this.state.addressCollapsed,
    });
  };

  shouldShowPaymentBtn() {
    const { acceptsPrivacy, acceptsSgContract, paymentMethod, paymentRequired } = this.state;

    if (!paymentRequired) {
      return false;
    }

    if (
      acceptsPrivacy &&
      acceptsSgContract &&
      !this.props.isPaypalExpress &&
      paymentMethod &&
      paymentMethod.name === PAYPAL
    ) {
      return false;
    }

    return true;
  }

  getPaypalButtonClassName = () => {
    const {
      acceptsPrivacy,
      acceptsSgContract,
      isLoadingPayPalCheckout,
      paymentMethod,
    } = this.state;

    if (
      acceptsPrivacy &&
      acceptsSgContract &&
      !this.props.isPaypalExpress &&
      paymentMethod &&
      paymentMethod.name === PAYPAL &&
      !isLoadingPayPalCheckout
    ) {
      return '';
    }

    // default class
    return 'paypal-checkout-hidden';
  };

  _renderPayPalCheckoutBtn() {
    const { cart, relay, onNextStep, intl } = this.props;

    this.setState({ isLoadingPayPalCheckout: true });
    BraintreeClientTokenMutation.commit(relay.environment, (error, token) => {
      if (!error) {
        initPaypalButton('paypal-checkout-btn', token, {
          checkoutStep: 2,
          variables: {
            amount: getRealAmount(cart.order.bill.totalBruttoPriceUnpayed),
            currency: cart.totalPrice.currency,
          },
          language: intl.locale,
          onAuthorize: nonce => onNextStep(this.state.paymentMethod, nonce),
          onCancel: () =>
            trackEvent('Checkout', 'Canceled payment', 'PayPal payment canceled'),
          onReady: () =>
            this.setState({
              isLoadingPayPalCheckout: false,
              hasPayPalCheckoutBtn: true,
            }),
        });
      }
    });
  }

  render() {
    const {
      acceptsPrivacy,
      acceptsSgContract,
      addressCollapsed,
      errorMsgId,
      hasPaypal,
      isLoadingPayPalCheckout,
      paymentMethod,
      paymentRequired,
      showSnackbar,
    } = this.state;

    const { cart, isPaypalExpress, onChangeAddress, onPreviousStep } = this.props;

    const { order } = cart;
    const hasPaymentBtn = this.shouldShowPaymentBtn();
    
    return (
      <div>
        {paymentRequired && (
          <CheckoutOrderingPaymentStepPaymentMethods
            ref={element => (this.paymentMethodDiv = element)}
            availablePaymentMethods={cart.availablePaymentMethods}
            isPaypalExpress={isPaypalExpress}
            selectedPaymentMethod={paymentMethod}
            onPaymentMethodChanged={this.handlePaymentMethodChanged}
          />
        )}
        {!addressCollapsed && (
          <CheckoutOrderingPaymentStepAddress
            address={order.address}
            onChangeAddress={onChangeAddress}
            onShowAddress={this.handleShowAddress}
          />
        )}

        <div className="row">
          <div className="col-xs-6 col-sm-3 col-sm-offset-3">
            <h2 className="order-payment-form-overview">
              <FormattedMessageWrappedInSpan
                id="checkoutOrderingPaymentStep.paymentBillTitle"
                defaultMessage="Bill overview"
              />
            </h2>
          </div>
          <div className="col-xs-6 col-xs-offset-4 col-sm-2 col-sm-offset-5">
            <div
              className="order-payment-form-address-expand"
              onClick={this.handleShowAddress}
            >
              {addressCollapsed && (
                <FormattedMessageWrappedInSpan
                  id="checkoutOrderingPaymentStep.showAddressButton"
                  defaultMessage="Show Address"
                />
              )}
            </div>
          </div>
        </div>
        <CheckoutBillTable 
          orderId={order.id} 
          bill={order.bill}
	  onCouponChanged={this.handleCouponChanged}
	/>
        <CheckoutOrderingPaymentStepCheckbox
          acceptsPrivacy={acceptsPrivacy}
          acceptsSgContract={acceptsSgContract}
          onPrivacyCheckboxChanged={this.handlePrivacyCheckboxChanged}
          onSgContractCheckboxChanged={this.handleSgContractCheckboxChanged}
        />
        <div className="row">
          <div className="order-payment-form-navigation col-xs-16 col-sm-10 col-sm-offset-3">
            <div className="checkout-back-button" onClick={onPreviousStep}>
              <FormattedMessageWrappedInSpan
                id="checkoutOrderingPaymentStep.backButton"
                defaultMessage="Back"
              />
            </div>
            {hasPaymentBtn && (
              <button
                className="primary"
                onClick={() => this.handleCompleteCheckout('pay')}
              >
                {this.props.intl.formatMessage(translations.nextButton)}
              </button>
            )}
            {!hasPaymentBtn && isLoadingPayPalCheckout && (
              <CircularProgress size={15} thickness={4} />
            )}
            {!hasPaymentBtn && !paymentRequired && (
              <button
                className="primary"
                onClick={() => this.handleCompleteCheckout('free')}
              >
                <FormattedMessageWrappedInSpan
                  id="checkoutOrderingPaymentStep.confirmFreeOrderButton"
                  defaultMessage="Buy"
                />
              </button>
            )}
            {hasPaypal && (
              <div className={this.getPaypalButtonClassName()} id="paypal-checkout-btn" />
            )}
          </div>
        </div>
        <Snackbar
          open={showSnackbar}
          message={this.props.intl.formatMessage(translations[errorMsgId])}
          onClose={this.handleCloseSnackbar}
        />
      </div>
    );
  }
}

const translations = defineMessages({
  paymentMethodNotDefinedError: {
    id: 'checkoutOrderingPaymentStep.paymentMethodNotDefinedError',
    defaultMessage: 'Please select a payment method',
  },
  privacyCheckboxNotCheckedError: {
    id: 'checkoutOrderingPaymentStep.privacyCheckboxNotCheckedError',
    defaultMessage: 'You have to accept our aggreements',
  },
  sgContractCheckboxNotCheckedError: {
    id: 'checkoutOrderingPaymentStep.sgContractCheckboxNotCheckedError',
    defaultMessage: 'You have to accept our contract',
  },
  nextButton: {
    id: 'checkoutOrderingPaymentStep.nextButton',
    defaultMessage: 'Pay now',
  },
});

CheckoutOrderingPaymentStep.propTypes = {
  isPaypalExpress: PropTypes.bool,
  onChangeAddress: PropTypes.func,
  onConfirmCheckout: PropTypes.func,
  onNextStep: PropTypes.func,
  onPreviousStep: PropTypes.func,
  cart: PropTypes.object,
  intl: PropTypes.object,
  relay: PropTypes.object,
};

export { CheckoutOrderingPaymentStep };

export default createFragmentContainer(injectIntl(CheckoutOrderingPaymentStep), {
  cart: graphql`
    fragment CheckoutOrderingPaymentStep_cart on Cart {
      id
      availablePaymentMethods {
        id
        name
      }
      order {
        id
        address {
          ...CheckoutOrderingPaymentStepAddress_address
        }
        bill {
          totalBruttoPrice
          totalBruttoPriceUnpayed
          ...CheckoutBillTable_bill
        }
        payment {
          paymentMethod {
            id
            name
          }
        }
        state
      }
      paymentRequired
      totalPrice
    }
  `,
});
